import { createContext, useContext, useState, type ReactNode } from "react";
import type {
  KeyMetricsResponse,
  FindingsSummaryResponse,
} from "../api/data-services/task-api";
import type {
  BrandsResponse,
  ChannelsResponse,
} from "../api/data-services/dashboard-api";

// Types
export interface DashboardState {
  keyMetrics: KeyMetricsResponse | null;
  findingsSummary: FindingsSummaryResponse | null;
  brands: BrandsResponse | null;
  channels: ChannelsResponse | null;
  isLoading: boolean;
  error: string | null;
}

interface DashboardContextType {
  dashboardState: DashboardState;
  setKeyMetrics: (metrics: KeyMetricsResponse) => void;
  setFindingsSummary: (findings: FindingsSummaryResponse) => void;
  setBrands: (brands: BrandsResponse) => void;
  setChannels: (channels: ChannelsResponse) => void;
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  resetDashboard: () => void;
}

const DashboardContext = createContext<DashboardContextType | undefined>(
  undefined,
);

interface DashboardProviderProps {
  children: ReactNode;
}

const initialState: DashboardState = {
  keyMetrics: null,
  findingsSummary: null,
  brands: null,
  channels: null,
  isLoading: false,
  error: null,
};

export const DashboardProvider: React.FC<DashboardProviderProps> = ({
  children,
}) => {
  const [dashboardState, setDashboardState] =
    useState<DashboardState>(initialState);

  const setKeyMetrics = (metrics: KeyMetricsResponse) => {
    setDashboardState((prev) => ({
      ...prev,
      keyMetrics: metrics,
      error: null,
    }));
  };

  const setFindingsSummary = (findings: FindingsSummaryResponse) => {
    setDashboardState((prev) => ({
      ...prev,
      findingsSummary: findings,
      error: null,
    }));
  };

  const setBrands = (brands: BrandsResponse) => {
    setDashboardState((prev) => ({
      ...prev,
      brands,
      error: null,
    }));
  };

  const setChannels = (channels: ChannelsResponse) => {
    // Transform "Banner" to "Static Banner"
    const transformedChannels: ChannelsResponse = {
      ...channels,
      channels: channels.channels.map((channel) => ({
        ...channel,
        channel_name:
          channel.channel_name === "Banner"
            ? "Static Banner"
            : channel.channel_name,
      })),
    };
    setDashboardState((prev) => ({
      ...prev,
      channels: transformedChannels,
      error: null,
    }));
  };

  const setIsLoading = (loading: boolean) => {
    setDashboardState((prev) => ({ ...prev, isLoading: loading }));
  };

  const setError = (error: string | null) => {
    setDashboardState((prev) => ({
      ...prev,
      error,
      isLoading: false,
    }));
  };

  const resetDashboard = () => {
    setDashboardState(initialState);
  };

  return (
    <DashboardContext.Provider
      value={{
        dashboardState,
        setKeyMetrics,
        setFindingsSummary,
        setBrands,
        setChannels,
        setIsLoading,
        setError,
        resetDashboard,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboard = (): DashboardContextType => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error("useDashboard must be used within a DashboardProvider");
  }
  return context;
};
